/**
 * classify.js — clasificador PURO de respuestas HTTP del push (PUSH-03).
 *
 * Vive aparte del drain para ser testeable en aislamiento: NO importa nada de la app (ni SQL, ni
 * transport); es una funcion pura sin efectos secundarios. El CALLER (outbox.js) computa next_attempt_at con
 * Date.now() — aqui NO se lee el reloj (mantiene la pureza y la testabilidad).
 *
 * Contrato: tasks/offline-v2/02-sync-contract.md §1 "Clasificacion de respuestas":
 *   2xx=done · 403/404=mapear a exito donde la tabla lo define (op-aware) · 401=re-auth ·
 *   429=backoff respetando Retry-After · 4xx permanente=dead-letter · 5xx/red=reintento con
 *   backoff exponencial [1s..1h], MAX_RETRIES=10.
 *
 * Quirks CONFIRMADOS del backend (02-sync-contract.md §1):
 *   - 403 en task.create/fromForms = la tarea ya existe (dedup por titulo) -> EXITO.
 *   - 403 en delete = no-owner -> dead-letter (NO es exito; solo 404-en-delete es exito).
 *   - 404 en delete = ya borrada -> EXITO.
 */

// Backoff exponencial LOCKED (ms): 1s, 3s, 10s, 30s, 2m, 10m, 1h (PUSH-01; identico a v1 outbox.js:27).
export const BACKOFF = [1000, 3000, 10000, 30000, 120000, 600000, 3600000]

// Tope de reintentos transitorios antes de dead-letter (PUSH-01/02).
export const MAX_RETRIES = 10

// Default de pausa por 429 si no viene Retry-After (v1 usaba THROTTLE_PAUSE_MS=60s).
const THROTTLE_DEFAULT_MS = 60000

// Reagenda por DEPENDENCIA interna (err.waitingDependency): corto, porque la dependencia suele
// drenar en el mismo reconnect (FIFO); se reagenda SIN consumir retries -> NUNCA dead-letterea
// por esperar (PR review F3/F6).
export const DEPENDENCY_RETRY_MS = 30000

/**
 * Backoff para el n-esimo reintento, saturado al ultimo elemento del array, con JITTER de ±20%.
 * El jitter evita el thundering herd: tras una caída del backend, todo el fleet reintentaría en
 * lock-step (los mismos escalones fijos) y lo estampidaría al recuperarse. Math.random NO es
 * criptográfico aquí — solo dispersa los reintentos en el tiempo, que es exactamente su uso correcto.
 * [review Hugo]
 */
export const backoffFor = (retries) => {
  const base = BACKOFF[Math.min(retries, BACKOFF.length - 1)]
  const jitter = base * 0.2 * (Math.random() * 2 - 1) // ±20%
  return Math.round(base + jitter)
}

/**
 * Lectura DEFENSIVA del status (el gotcha real, Pitfall 3 del research): el interceptor de
 * respuesta de axios (src/api/responser.js) rechaza con el objeto RESPONSE (status arriba), no
 * con el axios error (.response.status). Se leen AMBAS formas. null/undefined = sin respuesta
 * (red caida) -> rama transitoria.
 */
function readStatus (err) {
  if (err == null) return null
  return err.status ?? (err.response && err.response.status) ?? null
}

/** Lee Retry-After (segundos) de cualquiera de las dos formas; devuelve ms o null. */
function readRetryAfterMs (err) {
  const headers = (err && err.headers) ?? (err && err.response && err.response.headers) ?? null
  const raw = headers && (headers['retry-after'] ?? headers['Retry-After'])
  if (raw == null) return null
  const secs = Number(raw)
  return Number.isFinite(secs) && secs >= 0 ? secs * 1000 : null
}

/**
 * Clasifica una respuesta/error de push en un outcome `{ kind, ... }`.
 *
 * @param {*} statusOrErr  el error/response rechazado (lectura dual-shape) o un objeto { status }.
 * @param {{ op?: string }} ctx  contexto; ctx.op decide el mapeo de 403/404.
 * @returns {{ kind: 'done'|'success'|'dead'|'reauth'|'throttle'|'retry', retryAfterMs?: number }}
 */
export function classify (statusOrErr, ctx = {}) {
  const op = ctx.op
  const status = readStatus(statusOrErr)

  // ESPERA DE DEPENDENCIA (PR review F3/F6): un handler detectó que su op depende de OTRA fila del
  // outbox aún sin drenar (close esperando answers de la occurrence; hija de task esperando el
  // server_id del create). Reagenda tipo throttle (SIN retries++): esperar no es fallar.
  if (statusOrErr && statusOrErr.waitingDependency === true) {
    return { kind: 'throttle', retryAfterMs: DEPENDENCY_RETRY_MS }
  }

  // DEPENDENCIA MUERTA (PR review F6): la dependencia es IRRECUPERABLE (el create padre ya está
  // dead-letterado / no existe mapping posible). Fail-fast a dead: la pérdida se hace visible YA en
  // el drawer, en vez de quemar horas de backoff para terminar dead igual.
  if (statusOrErr && statusOrErr.dependencyDead === true) {
    return { kind: 'dead' }
  }

  // 2xx -> done (camino feliz). (status ya numerico tras readStatus.)
  if (status != null && status >= 200 && status < 300) {
    return { kind: 'done' }
  }

  // 403: op-aware. task.create/fromForms = "ya existe" -> exito; delete = no-owner -> dead.
  if (status === 403) {
    if (op === 'task.create') return { kind: 'success' } // dedup por titulo (403 = ya existe)
    if (op === 'task.delete' || op === 'task.deleteStandalone') return { kind: 'dead' } // no-owner
    // cualquier otro 403 es un 4xx permanente -> dead-letter (cae abajo).
  }

  // 404 en delete = ya borrada -> exito (idempotencia de borrado).
  if (status === 404 && (op === 'task.delete' || op === 'task.deleteStandalone')) {
    return { kind: 'success' }
  }

  // 401 -> re-auth. NUNCA dead-letter; el caller pausa + reencola con AUTH_RETRY_MS.
  if (status === 401) {
    return { kind: 'reauth' }
  }

  // 429 -> throttle respetando Retry-After (o default 60s).
  if (status === 429) {
    return { kind: 'throttle', retryAfterMs: readRetryAfterMs(statusOrErr) ?? THROTTLE_DEFAULT_MS }
  }

  // Cualquier otro 4xx permanente (422, 400, 409, 403-no-mapeado) -> dead-letter.
  // 422 -> dead por recomendacion del research: reintentar un 422 determinista solo quema
  // el budget de backoff y termina en dead de todos modos.
  if (status != null && status >= 400 && status < 500) {
    return { kind: 'dead' }
  }

  // 5xx -> reintento transitorio con backoff.
  if (status != null && status >= 500) {
    return { kind: 'retry' }
  }

  // null/undefined: red caida sin respuesta, O un handler lanzo un Error plano sin status
  // (ej. server_id no resuelto: el create padre aun no drena). TRANSITORIO -> retry, NUNCA dead.
  // FIFO por seq garantiza que el create padre drena antes (esta antes en la cola).
  return { kind: 'retry' }
}
